import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Search, Ticket as TicketIcon, User, Box, Loader2 } from "lucide-react";

export default function BuscaTicketsOps({ onAbrirTicket }) {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ tickets: [], clientes: [], equipamentos: [] });
  const [aberto, setAberto] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.length < 2) { setData({ tickets: [], clientes: [], equipamentos: [] }); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get("/tickets-ops/busca", { params: { q } });
        setData(d);
      } catch (e) { /* */ } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const totalResultados = data.tickets.length + data.clientes.length + data.equipamentos.length;

  return (
    <div ref={ref} className="relative w-full max-w-xl" data-testid="busca-tickets-ops">
      <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
      <Input
        className="pl-9 h-9 bg-white"
        placeholder="Buscar por nº ticket, cliente, CNPJ, serial, placa..."
        value={q}
        onChange={(e) => { setQ(e.target.value); setAberto(true); }}
        onFocus={() => setAberto(true)}
        data-testid="input-busca-integrada"
      />
      {aberto && q.length >= 2 && (
        <Card className="absolute top-full mt-1 left-0 right-0 z-50 max-h-96 overflow-y-auto shadow-lg">
          {loading && <div className="p-3 text-xs text-slate-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Buscando...</div>}
          {!loading && totalResultados === 0 && <div className="p-3 text-xs text-slate-500">Nenhum resultado encontrado.</div>}

          {data.tickets.length > 0 && (
            <div className="border-b">
              <div className="px-3 py-1 bg-slate-50 text-[10px] uppercase font-bold text-slate-500 flex items-center gap-1">
                <TicketIcon size={10} /> Tickets ({data.tickets.length})
              </div>
              {data.tickets.map((t) => (
                <button
                  key={t.id}
                  onClick={() => { onAbrirTicket?.(t.id); setAberto(false); setQ(""); }}
                  data-testid={`busca-ticket-${t.id}`}
                  className="w-full text-left p-2 hover:bg-orange-50 text-xs border-b last:border-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono">#{t.id.slice(0, 8)}</span>
                    <span className="text-[10px] uppercase">{t.tipo} · {t.status}</span>
                  </div>
                  <div className="text-slate-600">{t.cliente_nome}</div>
                </button>
              ))}
            </div>
          )}

          {data.clientes.length > 0 && (
            <div className="border-b">
              <div className="px-3 py-1 bg-slate-50 text-[10px] uppercase font-bold text-slate-500 flex items-center gap-1">
                <User size={10} /> Clientes ({data.clientes.length})
              </div>
              {data.clientes.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { navigate(`/app/central/${encodeURIComponent(c.id)}`); setAberto(false); setQ(""); }}
                  data-testid={`busca-cliente-${c.id}`}
                  className="w-full text-left p-2 hover:bg-orange-50 text-xs border-b last:border-0"
                >
                  <div className="font-semibold">{c.nome}</div>
                  <div className="text-slate-500 font-mono text-[10px]">{c.cnpj} · {c.uf || "—"}</div>
                </button>
              ))}
            </div>
          )}

          {data.equipamentos.length > 0 && (
            <div>
              <div className="px-3 py-1 bg-slate-50 text-[10px] uppercase font-bold text-slate-500 flex items-center gap-1">
                <Box size={10} /> Equipamentos ({data.equipamentos.length})
              </div>
              {data.equipamentos.map((e) => (
                <div
                  key={e.id}
                  data-testid={`busca-equip-${e.id}`}
                  className="p-2 text-xs border-b last:border-0"
                >
                  <div className="font-mono"><strong>{e.serial}</strong> · {e.placa || "—"}</div>
                  <div className="text-slate-500 text-[10px]">status: {e.status}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
